import React, { useEffect, useRef } from "react";
import { Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";

const DOT_ORANGE = "#F77F00";

const wsdotIcon = L.divIcon({
  className: "",
  html: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6));">
    <rect x="4.2" y="4.2" width="15.6" height="15.6" rx="2" transform="rotate(45 12 12)"
      fill="${DOT_ORANGE}" stroke="#0A0D0C" stroke-width="1.5"/>
    <rect x="10.9" y="6.5" width="2.2" height="7" rx="1" fill="#0A0D0C"/>
    <circle cx="12" cy="16.4" r="1.4" fill="#0A0D0C"/>
  </svg>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
  popupAnchor: [0, -11],
});

const CLOSURE_RE = /closed|closure/i;

export function isWsdotClosure(a) {
  return CLOSURE_RE.test(`${a.category || ""} ${a.headline || ""} ${a.description || ""}`);
}

const EVAC_BUFFER_MI = 10;

function haversineMi(lat1, lon1, lat2, lon2) {
  const R = 3958.8;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function geomCircle(geometry) {
  const pts = [];
  const walk = (c) => {
    if (Array.isArray(c) && typeof c[0] === "number") pts.push(c);
    else if (Array.isArray(c)) c.forEach(walk);
  };
  walk(geometry?.coordinates);
  if (!pts.length) return null;
  let minLat = 90, maxLat = -90, minLon = 180, maxLon = -180;
  for (const [lon, lat] of pts) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  }
  const cLat = (minLat + maxLat) / 2;
  const cLon = (minLon + maxLon) / 2;
  return { lat: cLat, lon: cLon, radiusMi: haversineMi(cLat, cLon, maxLat, maxLon) };
}

function evacCircles(evacAlerts, evacuationZones) {
  const circles = [];
  for (const e of evacAlerts || []) {
    if (e.active === false) continue;
    if (e.geometry) {
      const c = geomCircle(e.geometry);
      if (c) { circles.push(c); continue; }
    }
    if (e.latitude != null && e.longitude != null) {
      circles.push({ lat: e.latitude, lon: e.longitude, radiusMi: e.radius_mi || 1 });
    }
  }
  for (const f of evacuationZones?.features || []) {
    const c = geomCircle(f.geometry);
    if (c) circles.push(c);
  }
  return circles;
}

export function filterClosuresNearEvacs(alerts, evacAlerts, evacuationZones) {
  const closures = (alerts || []).filter(
    (a) => isWsdotClosure(a) && a.start_lat != null && a.start_lon != null
  );
  const circles = evacCircles(evacAlerts, evacuationZones);
  if (!circles.length) return [];
  return closures.filter((a) =>
    circles.some((c) => {
      const limit = c.radiusMi + EVAC_BUFFER_MI;
      if (haversineMi(a.start_lat, a.start_lon, c.lat, c.lon) <= limit) return true;
      if (a.end_lat && a.end_lon && haversineMi(a.end_lat, a.end_lon, c.lat, c.lon) <= limit) return true;
      return false;
    })
  );
}

function mpRange(a) {
  if (a.start_milepost == null) return null;
  const s = Math.round(a.start_milepost * 10) / 10;
  if (a.end_milepost != null && a.end_milepost !== a.start_milepost) {
    return `MP ${s} – ${Math.round(a.end_milepost * 10) / 10}`;
  }
  return `MP ${s}`;
}

export default function WsdotClosuresLayer({ alerts = [], evacAlerts = [], evacuationZones = null, focusWsdot = null }) {
  const closures = filterClosuresNearEvacs(alerts, evacAlerts, evacuationZones);
  const markerRefs = useRef({});
  useEffect(() => {
    if (!focusWsdot?.id) return;
    const t = setTimeout(() => markerRefs.current[focusWsdot.id]?.openPopup(), 900);
    return () => clearTimeout(t);
  }, [focusWsdot]);
  return (
    <>
      {closures.map((a) => (
        <React.Fragment key={`wsdot-${a.id}`}>
          {!!a.end_lat && !!a.end_lon && (a.end_lat !== a.start_lat || a.end_lon !== a.start_lon) && (
            <Polyline
              positions={[[a.start_lat, a.start_lon], [a.end_lat, a.end_lon]]}
              pathOptions={{ color: DOT_ORANGE, weight: 4, opacity: 0.7, dashArray: "8 6" }}
            />
          )}
          <Marker
            position={[a.start_lat, a.start_lon]}
            icon={wsdotIcon}
            data-testid={`wsdot-closure-marker-${a.id}`}
            ref={(r) => { markerRefs.current[a.id] = r; }}
          >
            <Popup>
              <div style={{ minWidth: 200 }}>
                <div style={{ fontWeight: 700, color: DOT_ORANGE, fontSize: 12 }}>
                  WSDOT · {(a.category || "CLOSURE").toUpperCase()}
                </div>
                <div style={{ fontWeight: 700, fontSize: 13, margin: "3px 0" }}>
                  {a.road ? `SR ${a.road}` : "State highway"}
                  {mpRange(a) ? ` · ${mpRange(a)}` : ""}
                </div>
                <div style={{ fontSize: 12, lineHeight: 1.45 }}>{a.headline}</div>
                {a.priority && (
                  <div style={{ fontSize: 10, opacity: 0.65, marginTop: 4 }}>
                    Priority: {a.priority}
                    {a.last_updated ? ` · updated ${new Date(a.last_updated).toLocaleDateString()}` : ""}
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        </React.Fragment>
      ))}
    </>
  );
}
